import { Outlet, useLocation, useNavigate, Link, NavLink } from 'react-router-dom';
import { useAppAuth } from './AuthContext.jsx';
import { logout } from '../services/auth.js';

// Application chrome: header + main content + footer. Keeps navigation
// consistent everywhere. Mobile uses a simple full-width layout.
const NAV = [
  { group: 'VEDENÍ', items: [
    { to: '/dashboard', icon: 'grid', label: 'Přehled', end: true },
    { to: '/dashboard/database', icon: 'user', label: 'Detail makléře' },
    { to: '/dashboard/level-assignment', icon: 'layers', label: 'Zařazení do úrovně' },
    { to: '/dashboard/kalendar', icon: 'cal', label: 'Kalendář a rytmus' },
    { to: '/dashboard/milniky', icon: 'flag', label: 'Milníky' },
    { to: '/dashboard/report', icon: 'mail', label: 'Týdenní report' },
  ] },
  { group: 'SPRÁVA', items: [
    { to: '/dashboard/sprava', icon: 'check', label: 'Co kdy udělat' },
    { to: '/dashboard/import', icon: 'upload', label: 'Import provizí' },
    { to: '/accounts', icon: 'key', label: 'Účty a skupiny' },
    { to: '/dashboard/pripominka', icon: 'bell', label: 'Denní připomínka' },
    { to: '/dashboard/parametry', icon: 'sliders', label: 'Parametry' },
    { to: '/dashboard/kpi', icon: 'chart', label: 'KPI' },
    { to: '/dashboard/kontakty', icon: 'user', label: 'Databáze kontaktů' },
    { to: '/dashboard/mapa', icon: 'map', label: 'Mapa systému' },
  ] },
];

const JCH_NAV = [
  { group: 'VEDENÍ', items: [
    { to: '/dashboard/cisla', icon: 'grid', label: 'Společná čísla', end: true },
    { to: '/dashboard/database', icon: 'user', label: 'Přehled makléře pro 1:1' },
    { to: '/dashboard/report', icon: 'mail', label: 'Týdenní report' },
    { to: '/dashboard/milniky', icon: 'flag', label: 'Milníky a pevné body' },
    { to: '/dashboard/kpi', icon: 'chart', label: 'KPI' },
    { to: '/dashboard/kontakty', icon: 'user', label: 'Databáze kontaktů' },
  ] },
  { group: 'SPRÁVA', items: [
    { to: '/dashboard/level-assignment', icon: 'layers', label: 'Zařazení do úrovně' },
    { to: '/accounts', icon: 'key', label: 'Účty a skupiny' },
    { to: '/dashboard/import', icon: 'upload', label: 'Import provizí' },
    { to: '/dashboard/parametry', icon: 'sliders', label: 'Parametry' },
  ] },
];

const ICONS = {
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  user: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 4-6 8-6s8 2 8 6',
  layers: 'M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5',
  cal: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  flag: 'M6 21V4M6 4h12l-2 4 2 4H6',
  mail: 'M3 6h18v12H3zM3 7l9 7 9-7',
  check: 'M5 12l5 5 9-10',
  upload: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  key: 'M8 14a4 4 0 110-8 4 4 0 010 8zM11 11l9 9M16 16l2-2',
  bell: 'M6 17V11a6 6 0 0112 0v6l2 2H4zM10 21h4',
  sliders: 'M4 7h10M18 7h2M4 17h2M10 17h10M14 5v4M6 15v4',
  chart: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  map: 'M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v14M15 6v14',
};

function NavIcon({ name }) {
  return (
    <svg className="jr-side__icon" viewBox="0 0 24 24" aria-hidden="true" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name] || ICONS.grid} />
    </svg>
  );
}

function BrandMark() {
  return (
    <span className="layout__logo layout__logo--mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 16l5-5 4 4 7-8" />
        <path d="M15 7h5v5" />
      </svg>
    </span>
  );
}

export default function Layout() {
  const { user } = useAppAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/register';
  const isAdmin = user?.role === 'JCH';
  const isLoggedIn = Boolean(user);
  const showAdmin = user?.role === 'JCH' || user?.role === 'JR';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  if (isAuthRoute) {
    return (
      <div className="layout layout--auth">
        <main className="layout__main layout__main--auth">
          <Outlet />
        </main>
      </div>
    );
  }

  if (showAdmin) {
    return (
      <div className="layout layout--side">
        <aside className="jr-side">
          <Link to={isAdmin ? '/dashboard/cisla' : '/dashboard'} className="layout__brand jr-side__brand">
            <BrandMark />
            <span className="jr-side__brandtext">
              <span className="layout__wordmark">Drill</span>
              <small className="jr-side__sub">powered by RE/MAX Partner</small>
            </span>
          </Link>
          {(isAdmin ? JCH_NAV : NAV).map((g) => (
            <nav key={g.group} aria-label={g.group}>
              <h2 className="jr-side__group">{g.group}</h2>
              {g.items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.end} className="jr-side__link">
                  <NavIcon name={i.icon} />
                  {i.label}
                </NavLink>
              ))}
            </nav>
          ))}
          <div className="jr-side__foot">
            <Link to="/my-account" className="jr-side__link"><NavIcon name="user" />Můj účet</Link>
            <button type="button" className="button button--quiet" onClick={handleLogout}>
              Odhlásit se
            </button>
          </div>
        </aside>
        <main className="layout__main jr-main">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="layout">
      <header className="layout__header">
        <Link to={isLoggedIn ? '/dashboard' : '/'} className="layout__brand">
          <span className="layout__logo" aria-hidden="true" />
          <span className="layout__wordmark">Drill</span>
        </Link>
        <nav className="layout__nav" aria-label="Primární navigace">
          {!isLoggedIn ? (
            <>
              <Link to="/">Domů</Link>
              <Link to="/login">Přihlášení</Link>
            </>
          ) : (
            <>
              <Link to="/dashboard">Přehled</Link>
              <Link to="/my-account" className="layout__account-link">
                <span className="layout__account-icon" aria-hidden="true">◉</span>
                Můj účet
              </Link>
              {showAdmin && (
                <>
                  <Link to="/dashboard/database">Databáze</Link>
                  <Link to="/dashboard/level-assignment">Zařazení do úrovně</Link>
                </>
              )}
              {isAdmin && <Link to="/accounts">Účty a skupiny</Link>}
              {isAdmin && (
                <>
                  <Link to="/dashboard/cisla">Společná čísla</Link>
                  <Link to="/dashboard/kpi">KPI systému</Link>
                  <Link to="/dashboard/report">Týdenní report</Link>
                  <Link to="/dashboard/milniky">Milníky</Link>
                </>
              )}
              <button type="button" className="button button--quiet layout__logout" onClick={handleLogout}>
                Odhlásit se
              </button>
            </>
          )}
        </nav>
      </header>

      <main className="layout__main">
        <Outlet />
      </main>

      <footer className="layout__footer">
        <span>© {new Date().getFullYear()} Drill · powered by RE/MAX Partner</span>
      </footer>
    </div>
  );
}
